import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { ActiveComponentChanged, ResetStateFromRouter } from 'src/app/shared/actions/eliqua.actions';
import { v4 as uuidv4 } from 'uuid';
import {
  AddRow,
  BatchNumberChanged,
  CheckbookPrefixChanged,
  DeleteAll,
  DeleteRow,
  EditCheckbookGroupTemplate,
  GetCheckbooksTypesSuccess,
  GetCouponSetSuccess,
  GroupTemplateCodeChanged,
  GroupTemplateCodeValidationStatus,
  InactiveChanged,
  LoadCheckbookGroupTemplateCols,
  PrefixChanged,
  RemarksChanged,
  SelectedCouponSetChanged,
  SelectedTypeChanged,
  SubmitCheckbookGroupTemplateData,
  UpdateCheckbookGroupTemplateSuccess,
} from '../actions/checkbook-group-template.actions';
import {
  CreateNewGroupTemplate,
  UpdateGroupTemplate,
} from '../actions/search-group-templates.actions';
import { CloseCheckbooksSidenav } from '../actions/sidenav.actions';
import { TableRow } from '../models/checkbook-group-template.model';
import { CheckbooksTypesResponse } from '../models/search-checkbooks.model';

export interface CheckbookGroupTemplateState extends EntityState<TableRow> {
  displayedColumns: { [label: string]: string };
  code: string;
  selectedTypeId: number | null;
  prefix: string;
  remarks: string;
  checkbooksTypes: CheckbooksTypesResponse[];
  couponSet: any;
  couponSetId: number | null;
  couponBatchNumber: number | null;
  bookCouponPrefix: number | null;
  isSubmittedData: boolean;
  createNewGroupTemplate: boolean;
  updateGroupTemplate: boolean;
  inactive: boolean;
  bookCouponGroupTemplateId: number | null;
  codeValidationError: boolean;
}

export const checkbookGroupTemplateAdapter = createEntityAdapter<TableRow>();

const initialState: CheckbookGroupTemplateState = checkbookGroupTemplateAdapter.getInitialState({
  displayedColumns: {},
  code: '',
  selectedTypeId: null,
  prefix: '',
  remarks: '',
  checkbooksTypes: [],
  couponSet: [],
  couponSetId: null,
  couponBatchNumber: null,
  bookCouponPrefix: null,
  isSubmittedData: false,
  createNewGroupTemplate: false,
  updateGroupTemplate: false,
  inactive: false,
  bookCouponGroupTemplateId: null,
  codeValidationError: false,
});

export const CheckbookGroupTemplateReducer = createReducer(
  initialState,
  on(GroupTemplateCodeChanged, (state, { code }) => ({
    ...state,
    code,
  })),
  on(SelectedTypeChanged, (state, { selectedTypeId }) => ({
    ...state,
    selectedTypeId,
  })),
  on(PrefixChanged, (state, { prefix }) => ({
    ...state,
    prefix,
  })),
  on(RemarksChanged, (state, { remarks }) => ({
    ...state,
    remarks,
  })),
  on(GetCheckbooksTypesSuccess, (state, { response }) => {
    const checkbooksTypes = response.map((e) => ({
      tablebookcoupontypeId: e.tablebookcoupontypeId,
      bookCouponType: e.bookCouponType,
    }));
    return {
      ...state,
      checkbooksTypes,
    };
  }),
  on(LoadCheckbookGroupTemplateCols, (state, { displayedColumns }) => ({
    ...state,
    displayedColumns,
  })),
  on(AddRow, (state) => {
    const newRow: TableRow = {
      id: uuidv4(),
      bookCouponPrefix: null,
      couponBatchNumber: null,
      couponSetId: null,
    };
    return checkbookGroupTemplateAdapter.addOne(newRow, state);
  }),
  on(DeleteRow, (state, { id }) => checkbookGroupTemplateAdapter.removeOne(id, state)),
  on(DeleteAll, (state) => checkbookGroupTemplateAdapter.removeAll(state)),
  on(GetCouponSetSuccess, (state, { response }) => ({
    ...state,
    couponSet: response.map((item: any) => ({
      couponSetId: item.couponSetId,
      title: item.title,
    })),
  })),
  on(SelectedCouponSetChanged, (state, { couponSetId, id }) => {
    return checkbookGroupTemplateAdapter.updateOne(
      {
        id,
        changes: {
          couponSetId,
        },
      },
      state,
    );
  }),
  on(BatchNumberChanged, (state, { couponBatchNumber, id }) => {
    return checkbookGroupTemplateAdapter.updateOne(
      {
        id,
        changes: {
          couponBatchNumber,
        },
      },
      state,
    );
  }),
  on(InactiveChanged, (state, { inactive }) => ({
    ...state,
    inactive,
  })),
  on(CheckbookPrefixChanged, (state, { bookCouponPrefix, id }) => {
    return checkbookGroupTemplateAdapter.updateOne(
      {
        id,
        changes: {
          bookCouponPrefix,
        },
      },
      state,
    );
  }),
  on(SubmitCheckbookGroupTemplateData, (state) => ({
    ...state,
    isSubmittedData: true,
  })),
  on(ActiveComponentChanged, (state) =>
    checkbookGroupTemplateAdapter.removeAll({
      ...state,
      displayedColumns: {},
      code: '',
      selectedTypeId: null,
      prefix: '',
      remarks: '',
      couponSetId: null,
      couponBatchNumber: null,
      bookCouponPrefix: null,
      isSubmittedData: false,
      createNewGroupTemplate: false,
      updateGroupTemplate: false,
      inactive: false,
    }),
  ),
  on(CloseCheckbooksSidenav, (state) =>
    checkbookGroupTemplateAdapter.removeAll({
      ...state,
      displayedColumns: {},
      code: '',
      selectedTypeId: null,
      prefix: '',
      remarks: '',
      couponSetId: null,
      couponBatchNumber: null,
      bookCouponPrefix: null,
      isSubmittedData: false,
      createNewGroupTemplate: false,
      updateGroupTemplate: false,
      inactive: false,
    }),
  ),
  on(CreateNewGroupTemplate, (state) =>
    checkbookGroupTemplateAdapter.removeAll({
      ...state,
      displayedColumns: {},
      code: '',
      selectedTypeId: null,
      prefix: '',
      remarks: '',
      couponSetId: null,
      couponBatchNumber: null,
      bookCouponPrefix: null,
      isSubmittedData: false,
      createNewGroupTemplate: true,
      updateGroupTemplate: false,
      inactive: false,
    }),
  ),
  on(UpdateGroupTemplate, (state) => ({
    ...state,
    updateGroupTemplate: true,
  })),
  on(EditCheckbookGroupTemplate, (state, { data }) => {
    const newEntities = data.bookCouponGroupTemplateLineList.map((lineItem: TableRow) => ({
      id: uuidv4(),
      couponSetId: lineItem.couponSetId ?? state.couponSetId,
      couponBatchNumber: lineItem.couponBatchNumber ?? state.couponBatchNumber,
      bookCouponPrefix: lineItem.bookCouponPrefix ?? state.bookCouponPrefix,
    }));

    let updatedState = checkbookGroupTemplateAdapter.setAll(newEntities, state);

    return {
      ...updatedState,
      displayedColumns: state.displayedColumns,
      code: data.code ? data.code : state.code,
      selectedTypeId: data.bookCouponTypeId ? data.bookCouponTypeId : state.selectedTypeId,
      prefix: data.bookCouponGroupPrefix ? data.bookCouponGroupPrefix : state.prefix,
      remarks: data.remarks ? data.remarks : state.remarks,
      isSubmittedData: false,
      createNewGroupTemplate: false,
      updateGroupTemplate: true,
      inactive: data.deleteFlag,
      bookCouponGroupTemplateId: data.bookCouponGroupTemplateId,
    };
  }),
  on(UpdateCheckbookGroupTemplateSuccess, (state, { response }) => ({
    ...state,
    code: response.payload.data.code,
    selectedTypeId: response.payload.data.bookCouponTypeId,
    prefix: response.payload.data.bookCouponGroupPrefix,
    remarks: response.payload.data.remarks,
    isSubmittedData: false,
    createNewGroupTemplate: false,
    updateGroupTemplate: true,
    inactive: response.payload.data.deleteFlag,
    bookCouponGroupTemplateId: response.payload.data.bookCouponGroupTemplateId,
  })),
  on(GroupTemplateCodeValidationStatus, (state, { isInvalid }) => ({
    ...state,
    codeValidationError: isInvalid,
  })),
  on(ResetStateFromRouter, () => ({
    ...initialState
  }))
);
